import * as store from '@attain-apps/fe/web/main/lib/store'

import type { Collection } from '../../../collection'
import type * as ecModels from '../../../models'

import type { Config } from './config'
import * as models from './models'
import type { Plugin } from './plugin'
import { State } from './state'

// TODO: ModePlugin -> if we ever need this, we should do the stuff here.
// Basically, we should have an inEditingMode flag to indicate when the entity
// collection as whole is in editing mode

/**
 * Core actions for this {@link Plugin}
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig<
  TTypeParams extends ecModels.TypeParams.Constraint,
>(collection: Collection<TTypeParams>, config: Config<TTypeParams>) {
  type EditRequest<
    TId extends ecModels.EntityId,
    TEditedEntity extends models.EditedEntity<any>,
  > = Readonly<Partial<models.Edit.WithId<TId, TEditedEntity>>> & {
    /**
     * Determines whether to mark the edit as the current entity
     *
     * Usually used to track the edit (update / creation) that the user is
     * currently working on (e.g. in a slideout or modal)
     *
     * Can specify {@link ecModels.Target} by passing an object with a `target`
     * property
     *
     * @default true
     * @example
     * // Makes this the current edit (default behavior)
     * { markCurrent: true }
     * // Avoids making this the current edit
     * { markCurrent: false }
     * // Makes this the current edit for the given target
     * { markCurrent: { target: 'some-target' } }
     */
    readonly makeCurrent?:
      | boolean
      | {
          /**
           * Forces the current edits before this action takes effect to be
           * discarded, if there are any
           *
           * @default false
           */
          readonly discardPrevious?: boolean
          /**
           * @default ```
           * ```
           * {@link ecModels.Target.DEFAULT}
           */
          readonly target?: ecModels.Target
        }
  }

  /**
   * __NOTE:__ overloads are needed to ensure that only one callback signature is
   * inferred at a time for the `edit` callback. Otherwise, the parameter is
   * inferred to be `any` type
   */
  function editActionDataTransformer(
    request: EditRequest<ecModels.EntityId, TTypeParams['entity']>,
  ): Required<NonNullable<typeof request>>
  function editActionDataTransformer(
    request: EditRequest<models.CreationId, TTypeParams['createEntity']>,
  ): Required<NonNullable<typeof request>>
  function editActionDataTransformer(
    request?: EditRequest<ecModels.EntityId, models.EditedEntity<TTypeParams>>,
  ): Required<NonNullable<typeof request>>

  function editActionDataTransformer({
    edit,
    id = collection.plugins.currentEntityPlugin.defaultCurrentId ??
      models.CreationId.generate(),
    makeCurrent = true,
  }: EditRequest<ecModels.EntityId, models.EditedEntity<TTypeParams>> = {}) {
    return {
      edit,
      id,
      makeCurrent,
    }
  }
  return {
    /**
     * Adds or updates a pending edit for an entity.
     *
     * Calling with no arguments or an empty object starts a new, empty
     * creation.
     *
     * The value your edit is applied on top of depends on how you call it:
     * - __With an `edit`__ (callback or object): builds on top of any edit
     *   already pending for that `id`. If none is pending yet, a callback
     *   receives `undefined` and an object edit is used as-is - the entity's
     *   last saved value is __not__ pulled in for you.
     * - __With no `edit`__ (just an `id`): pre-fills the pending edit with the
     *   entity's last saved value.
     *
     * @example
     * // Changing one field via a callback, without pre-filling first:
     * // the callback receives `undefined`, so whatever it returns becomes
     * // the ENTIRE entity on submit - other fields are lost.
     * edit({ id, edit: () => ({ name: 'New name' }) })
     * @example
     * // Pre-fill, then edit: the callback receives the entity's last saved
     * // value, so unrelated fields are kept.
     * edit({ id }) // pre-fill
     * edit({ id, edit: entity => ({ ...entity, name: 'New name' }) })
     */
    edit: store.actions.standalone({
      data: editActionDataTransformer,
      reducerDeprecated: (state, { edit, id }, globalState) => {
        // If no id is passed, we use the current entity, if there is a current
        // entity. Otherwise, we create a new creation ID
        state = { ...state, submissionError: undefined }
        // TODO: find a way to make this DRY
        if (models.CreationId.isCreationId(id)) {
          let editedCreation = models.Edit.appliedTo(
            state.creationsLookup[id],
            edit ?? { input: {} },
          )
          if (config.newCreation && !state.creationsLookup[id]) {
            // If we have a newCreation callback, and this is a new creation, we
            // use it here
            editedCreation = config.newCreation(editedCreation)
          }
          if (editedCreation) {
            state.creationsLookup = {
              ...state.creationsLookup,
              // NOTE: it is possible that we end up with a partial entity (which
              // could be a problem for creation), but the best thing to do in
              // that case is just proceed and hope that the user either makes
              // additional edits or the API or backend handle the partial entity
              [id]: editedCreation satisfies Partial<
                TTypeParams['createEntity']
              > as TTypeParams['createEntity'],
            }
          }
        } else {
          const editedUpdate = models.Edit.appliedTo(
            state.updatesLookup[id],
            edit ?? {
              // Gets the value of the current entity from the cache
              input: collection.plugins.cachePlugin.feature.selectors
                .getCachedEntity(id)
                .asSelector(globalState),
            },
          )
          if (editedUpdate) {
            state.updatesLookup = {
              ...state.updatesLookup,
              // NOTE: it is possible that we end up with a partial entity, but
              // the best thing to do in that case is just proceed and hope that
              // the user either makes additional edits or the API or backend
              // handle the partial entity
              [id]: editedUpdate satisfies Partial<
                TTypeParams['createEntity']
              > as TTypeParams['createEntity'],
            }
          }
        }
        return state
      },
    }),
    /**
     * Marks one or more entities to be deleted on submit
     *
     * Can be undone using discard
     */
    markForDeletion: store.actions.standalone({
      data: ({
        id,
        ids,
      }: ExactlyOneOf<{
        readonly id: ecModels.EntityId
        readonly ids: readonly ecModels.EntityId[]
      }>) => ({ ids: ids ?? [id] }),
      reducerDeprecated: (state, { ids }) => {
        // Must make a Mutable version of deletions to remove from
        const deletions = new Set(state.deletions)
        for (const id of ids) {
          deletions.add(id)
        }

        return { ...state, deletions, submissionError: undefined }
      },
    }),
    /**
     * Submits edits to be executed
     */
    submit: store.actions.request({
      input: store.actions.Data.asIs<EditOptions>(),
      output:
        store.actions.Data.asIs<models.EditsSelection.Result<TTypeParams>>(),
      reducers: {
        request: ({ state }): State => State.onRequest(state),
        success: ({ state, data: { response } }) =>
          State.onSuccess(response, state),
        error: ({ state, data: { error } }): State =>
          State.onError(error, state),
        cancellation: ({ state }): State => State.onCancellation(state),
      },
    }),
    /**
     * Discards edits from the state
     */
    discard: store.actions.request({
      input: store.actions.Data.asIs<EditOptions>(),
      output:
        store.actions.Data.asIs<models.EditsSelection.Result<TTypeParams>>(),
      reducers: {
        success: ({ state, data: { response } }) =>
          State.onSuccess(response, state),
      },
    }),
  } as const satisfies store.actions.Config.Constraint<State<TTypeParams>>

  type EditOptions = models.EditsSelection & {
    /**
     * Determines whether to skip unsetting the current entity. By default,
     * current entity is unset after update. Sometimes we need the entity to
     * stay selected e. g. when editing entity on details page.
     *
     * @default false
     */
    skipUnset?: boolean
  }
}
